import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';
import type { PlayerPoolTier } from '@sorare/core/src/lib/playerPoolTiers';
import { leagueRewardPoolVariables } from '@sorare/core/src/lib/playerPoolTiers';

import { PlayerWithSupply } from '#src/components/rewards/PlayerWithSupply/index.tsx';

import type {
  RewardsFixtureLeagueTierRarityPageQuery,
  RewardsFixtureLeagueTierRarityPageQueryVariables,
} from './__generated__/index.graphql.ts';

const REWARDS_FIXTURE_LEAGUE_TIER_RARITY_PAGE_QUERY = gql`
  query RewardsFixtureLeagueTierRarityPageQuery(
    $league: String!
    $rarity: String!
    $quality: String
    $gameplayTier: GameplayTier
  ) {
    so5 {
      so5LeaderboardGroupInterface(slug: $league) {
        slug
        ... on So5League {
          slug
          rewardPool(
            rarity: $rarity
            quality: $quality
            gameplayTier: $gameplayTier
          ) {
            slug
            ...PlayerWithSupply_playerWithSupply
          }
          ...PlayerWithSupply_so5League
        }
      }
    }
  }
  ${PlayerWithSupply.fragments.playerWithSupply}
  ${PlayerWithSupply.fragments.so5League}
` as TypedDocumentNode<
  RewardsFixtureLeagueTierRarityPageQuery,
  RewardsFixtureLeagueTierRarityPageQueryVariables
>;

type Props = {
  league: string;
  tier: PlayerPoolTier;
  rarity: Rarity;
};

export const RewardsFixtureLeagueTierRarityPage = ({
  league,
  tier,
  rarity,
}: Props) => {
  const { data, loading } = useQuery(
    REWARDS_FIXTURE_LEAGUE_TIER_RARITY_PAGE_QUERY,
    {
      variables: {
        league,
        rarity,
        ...leagueRewardPoolVariables(tier),
      },
    }
  );

  const { so5LeaderboardGroupInterface: so5League } = data?.so5 || {};

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!so5League || !isType(so5League, 'So5League')) {
    return null;
  }

  return (
    <PlayerWithSupply
      so5League={so5League}
      players={so5League.rewardPool}
      rarity={rarity}
    />
  );
};
